import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { dispatchShopCreditBalanceRefresh } from '../constants';
import { ACTION, PAGE } from '@kioskgaming/ui';

type Variant = 'success' | 'cancelled' | 'error';

function useReturnParams() {
  const [searchParams] = useSearchParams();
  return useMemo(
    () => ({
      transactionId:
        searchParams.get('transaction_id') ||
        searchParams.get('transactionId') ||
        '',
      status: searchParams.get('status') || '',
      message: searchParams.get('message') || '',
    }),
    [searchParams],
  );
}

function Content({ variant }: { variant: Variant }) {
  const { transactionId, status, message } = useReturnParams();

  if (variant === 'success') {
    const completed = status.toLowerCase() === 'completed';
    const pending = status.toLowerCase() === 'pending' || !status;

    return (
      <>
        <h1 className="text-xl font-bold text-stone-900">Payment return</h1>
        {transactionId ? (
          <p className="mt-2 font-mono text-sm text-stone-600 break-all">Order: {transactionId}</p>
        ) : null}
        <p className="mt-4 text-stone-700">
          {completed
            ? 'Your payment was recorded as completed. Your Shop credit balance should update shortly.'
            : pending
              ? 'If you just finished checkout, the payment may still be confirming. Refresh the Dashboard in a moment or open Credit transactions.'
              : `Status from provider redirect: ${status || 'unknown'}. Check ${PAGE.creditTransactions} or try again if something looks wrong.`}
        </p>
      </>
    );
  }

  if (variant === 'cancelled') {
    return (
      <>
        <h1 className="text-xl font-bold text-stone-900">Payment cancelled</h1>
        {transactionId ? (
          <p className="mt-2 font-mono text-sm text-stone-600 break-all">Order: {transactionId}</p>
        ) : null}
        <p className="mt-4 text-stone-700">No charge was completed. You can start a new purchase anytime.</p>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-bold text-stone-900">Payment error</h1>
      <p className="mt-4 text-red-700">{message || 'Something went wrong processing the return link.'}</p>
    </>
  );
}

function FooterLinks() {
  return (
    <div className="mt-8 flex flex-wrap gap-3 text-sm">
      <Link to="/" className="font-semibold text-emerald-700 hover:text-emerald-900">
        {ACTION.backToDashboard}
      </Link>
      <Link to="/transactions" className="font-semibold text-emerald-700 hover:text-emerald-900">
        {PAGE.creditTransactions}
      </Link>
    </div>
  );
}

function RefreshBalanceOnPaymentSuccess() {
  useEffect(() => {
    dispatchShopCreditBalanceRefresh();
  }, []);
  return null;
}

export function ShopPaymentSuccessPage() {
  return (
    <AppLayout contentFullWidth>
      <RefreshBalanceOnPaymentSuccess />
      <Content variant="success" />
      <FooterLinks />
    </AppLayout>
  );
}

export function ShopPaymentCancelledPage() {
  return (
    <AppLayout contentFullWidth>
      <Content variant="cancelled" />
      <FooterLinks />
    </AppLayout>
  );
}

export function ShopPaymentErrorPage() {
  return (
    <AppLayout contentFullWidth>
      <Content variant="error" />
      <FooterLinks />
    </AppLayout>
  );
}
